/**
 * Página /relatorios — dashboard executivo agregado.
 *
 * Tela read-only que consolida métricas de TODAS as vagas encerradas
 * no período selecionado. Apresenta:
 *  - 4 KPIs no topo (vagas encerradas, contratações, taxa, custo total)
 *  - Heatmap de contratações por mês (últimos 12 meses, sempre)
 *  - Funil agregado (donut chart)
 *  - Top departamentos (barras horizontais)
 *  - Origem dos candidatos (barras verticais)
 *  - Tabela das contratações recentes com link pro candidato/vaga
 *
 * Endpoint único: GET /api/reports/executive-summary?period={month|quarter|year|all}
 */
import axios from "axios";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  TrendingUp,
  Users,
  Briefcase,
  DollarSign,
  Calendar,
  Loader2,
  Trophy,
  Award,
  Clock,
} from "lucide-react";
import { API } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const PERIODS = [
  { value: "month", label: "Último mês" },
  { value: "quarter", label: "Último trimestre" },
  { value: "year", label: "Último ano" },
  { value: "all", label: "Tudo" },
];

const FUNNEL_COLORS = {
  contratado: "#f59e0b",
  aprovado: "#10b981",
  em_entrevista: "#3b82f6",
  contatado: "#8b5cf6",
  analisado: "#94a3b8",
  rejeitado: "#f43f5e",
  pendente: "#cbd5e1",
  erro: "#475569",
};

const STATUS_LABELS = {
  contratado: "Contratado",
  aprovado: "Aprovado",
  em_entrevista: "Em entrevista",
  contatado: "Contatado",
  analisado: "Analisado",
  rejeitado: "Rejeitado",
  pendente: "Pendente",
  erro: "Erro",
};

const fmtCurrency = (v) => {
  if (v === null || v === undefined) return "—";
  try {
    return Number(v).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
      maximumFractionDigits: 0,
    });
  } catch {
    return `R$ ${v}`;
  }
};

const fmtNumber = (v) => {
  if (v === null || v === undefined) return "—";
  return Number(v).toLocaleString("pt-BR");
};

const KpiCard = ({ icon: Icon, label, value, accent = "violet", hint = "" }) => {
  const accents = {
    violet: "from-violet-500 to-violet-700 text-violet-900 border-violet-200 bg-violet-50",
    emerald: "from-emerald-500 to-emerald-700 text-emerald-900 border-emerald-200 bg-emerald-50",
    amber: "from-amber-500 to-amber-700 text-amber-900 border-amber-200 bg-amber-50",
    blue: "from-blue-500 to-blue-700 text-blue-900 border-blue-200 bg-blue-50",
  };
  const klass = accents[accent] || accents.violet;
  const [bgFrom, bgTo, text, border, fill] = klass.split(" ");
  return (
    <div
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`bg-white border ${border} rounded-xl p-4 shadow-sm`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${bgFrom} ${bgTo} text-white flex items-center justify-center shadow-md`}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            {label}
          </div>
          <div className={`text-2xl font-bold ${text} tabular-nums leading-tight`}>
            {value}
          </div>
          {hint && (
            <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>
          )}
        </div>
      </div>
    </div>
  );
};

const ReportsPage = () => {
  const [period, setPeriod] = useState("year");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const periodLabel = PERIODS.find((p) => p.value === period)?.label || "";
  useDocumentTitle(`Relatórios — ${periodLabel}`);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axios
      .get(`${API}/reports/executive-summary?period=${period}`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.detail || "Erro ao carregar relatório");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  const kpis = data?.kpis || {};
  const heatmap = data?.heatmap || [];
  const funnel = data?.funnel || {};
  const topDepts = data?.top_departments || [];
  const sources = data?.sources || [];
  const recentHires = data?.recent_hires || [];

  // Build funnel pie data
  const funnelData = Object.entries(funnel).map(([key, value]) => ({
    name: STATUS_LABELS[key] || key,
    value,
    key,
  }));

  const isEmpty =
    !loading &&
    !error &&
    kpis.total_closed_jobs === 0 &&
    kpis.total_hires === 0;

  return (
    <div data-testid="reports-page" className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-violet-600" />
            Relatórios executivos
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Visão consolidada de todas as vagas encerradas no período. Compartilhe com a diretoria.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-500 font-semibold">
            Período:
          </label>
          <select
            data-testid="period-selector"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="text-sm border border-slate-300 rounded-md px-3 py-1.5 bg-white"
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Calculando métricas…
        </div>
      ) : error ? (
        <div className="bg-rose-50 border border-rose-200 rounded-md p-4 text-sm text-rose-700">
          {error}
        </div>
      ) : isEmpty ? (
        <div
          data-testid="reports-empty"
          className="bg-white border border-slate-200 rounded-xl p-12 text-center"
        >
          <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-slate-700">
            Nenhuma vaga encerrada no período
          </h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Conforme você fechar processos e marcar candidatos como contratados, as métricas
            consolidadas vão aparecer aqui automaticamente.
          </p>
        </div>
      ) : (
        <>
          {/* ── KPIs ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              icon={Briefcase}
              label="Vagas encerradas"
              value={fmtNumber(kpis.total_closed_jobs || 0)}
              accent="violet"
            />
            <KpiCard
              icon={Trophy}
              label="Contratações"
              value={fmtNumber(kpis.total_hires || 0)}
              accent="amber"
              hint={
                kpis.hire_rate !== null && kpis.hire_rate !== undefined
                  ? `${kpis.hire_rate}% das vagas`
                  : ""
              }
            />
            <KpiCard
              icon={Clock}
              label="Time-to-hire médio"
              value={
                kpis.avg_time_to_hire !== null
                  ? `${kpis.avg_time_to_hire}d`
                  : "—"
              }
              accent="blue"
              hint={
                kpis.fastest_hire_days !== null &&
                kpis.slowest_hire_days !== null
                  ? `${kpis.fastest_hire_days}d–${kpis.slowest_hire_days}d`
                  : ""
              }
            />
            <KpiCard
              icon={DollarSign}
              label="Custo de contratação"
              value={fmtCurrency(kpis.total_cost || 0)}
              accent="emerald"
              hint={
                kpis.avg_salary !== null
                  ? `${fmtCurrency(kpis.avg_salary)} médio`
                  : ""
              }
            />
          </div>

          {/* ── Heatmap mensal ── */}
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-violet-600" />
              <h2 className="text-sm font-semibold text-slate-900">
                Contratações por mês (últimos 12 meses)
              </h2>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={heatmap}
                data-testid="heatmap-chart"
                margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={{ stroke: "#cbd5e1" }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  axisLine={{ stroke: "#cbd5e1" }}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: 12,
                    borderRadius: 6,
                    border: "1px solid #cbd5e1",
                  }}
                  cursor={{ fill: "rgba(139, 92, 246, 0.08)" }}
                />
                <Bar
                  dataKey="count"
                  fill="#8b5cf6"
                  radius={[4, 4, 0, 0]}
                  name="Contratações"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* ── Funil agregado ── */}
            {funnelData.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-violet-600" />
                  <h2 className="text-sm font-semibold text-slate-900">
                    Funil agregado
                  </h2>
                </div>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={funnelData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      stroke="#fff"
                      strokeWidth={2}
                    >
                      {funnelData.map((entry) => (
                        <Cell
                          key={entry.key}
                          fill={FUNNEL_COLORS[entry.key] || "#94a3b8"}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v, n) => [`${v} candidatos`, n]}
                      contentStyle={{
                        fontSize: 12,
                        borderRadius: 6,
                        border: "1px solid #cbd5e1",
                      }}
                    />
                    <Legend
                      iconSize={10}
                      wrapperStyle={{ fontSize: 11, paddingTop: 10 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* ── Top departamentos ── */}
            {topDepts.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Briefcase className="w-4 h-4 text-violet-600" />
                  <h2 className="text-sm font-semibold text-slate-900">
                    Departamentos que mais contrataram
                  </h2>
                </div>
                <ul className="space-y-2">
                  {topDepts.map((d) => (
                    <li
                      key={d.name}
                      data-testid={`dept-${d.name}`}
                      className="bg-slate-50 border border-slate-200 rounded-md p-2.5"
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-sm font-semibold text-slate-900 truncate">
                          {d.name}
                        </span>
                        <span className="text-xs text-slate-500 tabular-nums shrink-0">
                          {d.hires} contratação{d.hires !== 1 ? "ões" : ""}
                          {" de "}
                          {d.closed_jobs} vaga{d.closed_jobs !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full"
                            style={{
                              width: `${d.closed_jobs > 0 ? Math.round((d.hires / d.closed_jobs) * 100) : 0}%`,
                            }}
                          />
                        </div>
                        {d.avg_time_to_hire !== null && (
                          <span className="text-[10px] text-slate-500 shrink-0">
                            {d.avg_time_to_hire}d médio
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* ── Recent hires ── */}
          {recentHires.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Award className="w-4 h-4 text-violet-600" />
                <h2 className="text-sm font-semibold text-slate-900">
                  Contratações recentes
                </h2>
              </div>
              <ul className="divide-y divide-slate-100">
                {recentHires.map((h) => (
                  <li
                    key={h.candidate_id}
                    data-testid={`hire-row-${h.candidate_id}`}
                    className="py-2.5 flex items-center gap-3"
                  >
                    <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center shadow">
                      <Trophy className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-900 truncate">
                        {h.candidate_name}
                      </div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {h.job_title}
                        {h.hired_by_user_name && (
                          <span className="text-slate-400">
                            {" · contratado por "}
                            {h.hired_by_user_name}
                          </span>
                        )}
                      </div>
                    </div>
                    {h.hired_salary && (
                      <span className="text-xs font-bold text-emerald-700 tabular-nums shrink-0">
                        {fmtCurrency(h.hired_salary)}
                      </span>
                    )}
                    <Link
                      to={`/vagas/${h.job_id}`}
                      className="shrink-0 text-[11px] text-violet-700 hover:underline font-medium"
                    >
                      Ver relatório →
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Sources ── */}
          {sources.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-violet-600" />
                <h2 className="text-sm font-semibold text-slate-900">
                  Origem dos candidatos
                </h2>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={sources}
                  layout="vertical"
                  margin={{ top: 5, right: 20, left: 80, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    allowDecimals={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11, fill: "#64748b" }}
                    width={80}
                  />
                  <Tooltip
                    formatter={(v) => [`${v} candidatos`, "Total"]}
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: 6,
                      border: "1px solid #cbd5e1",
                    }}
                  />
                  <Bar
                    dataKey="count"
                    fill="#0ea5e9"
                    radius={[0, 4, 4, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReportsPage;
